import {
  DealItemCreateInput,
  CreateDealItemDocument,
} from '@/lib/graphql/generated/graphql';
import { useMutation } from '@apollo/client/react';

import { useDealNotEditableHandler } from './useDealNotEditableHandler';

// 1. Liste des clés « prix » à multiplier par 100
const priceKeys = [
  'buyingPrice',
  'choosePrice',
  'discountedPrice',
  'listPrice',
  'potentialSalesVolume',
  'shippingPrice',
] as const;

type PriceKey = (typeof priceKeys)[number];

/**
 * Transforme les champs monétaires et pourcentages avant envoi
 */
function transformCreate(item: DealItemCreateInput): DealItemCreateInput {
  const payload: DealItemCreateInput = { ...item };

  // ×100 sur les prix
  priceKeys.forEach((key: PriceKey) => {
    const v = item[key];
    if (typeof v === 'number') {
      payload[key] = Math.round(v * 100);
    }
  });

  // % → fraction
  if (item.chooseDiscount != null) {
    payload.chooseDiscount = item.chooseDiscount / 100;
  }

  if (item.marginPercentage != null) {
    payload.marginPercentage = item.marginPercentage / 100;
  }

  return payload;
}

/**
 * Hook pour créer un seul DealItem
 */
export function useCreateDealItem(dealId: string) {
  const [createDealItemMutation, { loading, error, data }] = useMutation(
    CreateDealItemDocument
  );
  const { handleIfDealNotEditable } = useDealNotEditableHandler();

  /**
   * Crée un produit pour le deal donné
   */
  const createItem = async (item: DealItemCreateInput) => {
    const payload = transformCreate(item);
    const result = await createDealItemMutation({
      variables: {
        dealId,
        item: payload,
      },
    });

    const mutationResult = result.data?.createDealItem;
    if (mutationResult?.__typename === 'DomainException') {
      handleIfDealNotEditable(mutationResult.code);
      return { id: undefined };
    }

    const id =
      mutationResult?.__typename === 'MutationSuccess'
        ? mutationResult.id
        : undefined;
    return { id };
  };

  return { createItem, loading, error, data };
}
